import { useQuery } from "@tanstack/react-query";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from "react";
import { identifyUser, initAnalytics } from "./analytics";

const LS_EVENT = "ls-sync";

function readLS<T>(name: string, initialState: T): T {
  try {
    const raw = localStorage.getItem(name);
    return raw !== null ? JSON.parse(raw) : initialState;
  } catch {
    return initialState;
  }
}

export function useStateLS<T>(
  name: string,
  initialState: T,
): [T, Dispatch<SetStateAction<T>>] {
  const subscribe = useCallback(
    (cb: () => void) => {
      const handler = (e: Event) => {
        if ((e as CustomEvent).detail === name) cb();
      };
      window.addEventListener(LS_EVENT, handler);
      return () => window.removeEventListener(LS_EVENT, handler);
    },
    [name],
  );

  const getSnapshot = useCallback(() => localStorage.getItem(name), [name]);

  const raw = useSyncExternalStore(subscribe, getSnapshot);
  const value: T = raw !== null ? JSON.parse(raw) : initialState;

  const setValueLS: Dispatch<SetStateAction<T>> = useCallback(
    (newValue) => {
      const prev = readLS(name, initialState);
      const resolved =
        typeof newValue === "function"
          ? (newValue as (prevState: T) => T)(prev)
          : newValue;
      localStorage.setItem(name, JSON.stringify(resolved));
      window.dispatchEvent(new CustomEvent(LS_EVENT, { detail: name }));
    },
    [name, initialState],
  );

  return [value, setValueLS];
}

export type UserTier = "internal" | "partner" | "community";
export type UserCluster = "base" | "pro";

export interface MySelf {
  id: number;
  name: string;
  email: string;
  isStaff: boolean;
  isSuperuser: boolean;
  canUseExperimentalFeatures: boolean;
  userTier: UserTier;
  canSeeCategories: boolean;
  mixpanelToken: string | null;
  taskResultExpiresDays: number;
  /** Version of the currently active ToS, or null if none is published. */
  tosVersion: string | null;
  /**
   * Engagement segment from the onboarding survey. `null` means we have not asked, or they
   * have not answered — deliberately not folded into "base", since the two mean different
   * things once this reaches Mixpanel.
   */
  cluster: UserCluster | null;
  /** Whether the onboarding survey should pop on this visit. See OnboardingSurveyDialog. */
  onboardingSurveyDue: boolean;
}

const defaultUser: MySelf = {
  id: 0,
  name: "",
  email: "",
  isStaff: false,
  isSuperuser: false,
  canUseExperimentalFeatures: false,
  userTier: "community",
  canSeeCategories: false,
  mixpanelToken: null,
  taskResultExpiresDays: 30,
  tosVersion: null,
  cluster: null,
  // Load-bearing `false`: `useUser()` serves this placeholder before /api/myself/ settles,
  // and it is indistinguishable from a real payload. Defaulting to `true` here would flash
  // the survey on every cold load. The gate also guards on `id !== 0`; this is the backstop.
  onboardingSurveyDue: false,
};

export function useUser() {
  const query = useQuery<MySelf>({
    queryKey: ["myself"],
    queryFn: () => fetch("/api/myself/").then((res) => res.json()),
    placeholderData: defaultUser,
  });

  useEffect(() => {
    if (query.data?.id) {
      if (query.data.mixpanelToken) {
        initAnalytics(query.data.mixpanelToken);
      }
      identifyUser(query.data);
    }
  }, [query.data]);

  return { ...query, data: query.data ?? defaultUser };
}

type Message = {
  action: "go-to-line";
  line: number;
};

export function sendMessage(target: Window, message: Message) {
  const uuid = crypto.randomUUID();
  let count = 0;

  const send = () => {
    count += 1;
    target.postMessage({ uid: uuid, ...message }, "*");
  };

  let interval: NodeJS.Timeout | null = null;
  setTimeout(() => {
    interval = setInterval(() => {
      send();
      if (count >= 10 && interval != null) {
        clearInterval(interval);
      }
    }, 500);
  }, 500);

  window.addEventListener("message", (event) => {
    if (
      event.data.action === "handled" &&
      event.data.uid === uuid &&
      interval != null
    ) {
      clearInterval(interval);
    }
  });

  send();
}

export function durationAsHumanReadable(duration: number): string {
  const seconds = duration % 60,
    minutes = Math.floor(duration / 60);

  return `${minutes}m ${seconds}s`;
}

export function nonEmpty(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
}
